import React from "react";
import { Card } from "@UI/Components/Card/Card";
import { OrderDescriptionProps } from "./types";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { ServiceOfferItem } from "./OrderDescriptionItems/ServiceOfferItem";
import { List } from "@UI/Components/List/List";
import { Total } from "./Total/Total";
import { SubscriptionItem } from "./OrderDescriptionItems/SubscriptionItem";
import { useCurrentBasket } from "@modules/Purchasing/hooks/useCurrentBasket";
import { Navigate } from "@Libs/Router/Components/Navigate/Navigate";
import { useCheckout } from "@modules/Purchasing/hooks/useCheckout";
import { IUnsavedBasket } from "@modules/Purchasing/services/baskets";
import { AdvancePaymentItem } from "./OrderDescriptionItems/AdvancePaymentItem";
import { ServiceFees } from "./OrderDescriptionItems/ServiceFees";
import { Credits } from "./OrderDescriptionItems/Credits";
import { IErrorResponse } from "@api/createApi";
import { v4 as uuidv4 } from "uuid";
import { useDataLayer } from "@Libs/DataLayer/hooks/useDataLayer";
import { useOrderDescription } from "./useOrderDescription";
import { Savings } from "./OrderDescriptionItems/Savings";

export const OrderDescription = ({
  order,
  colorScheme,
  isLoading,
  preview,
  ...props
}: OrderDescriptionProps) => {
  const { currentBasket, saveBasket } = useCurrentBasket();
  const { rootRoute } = useCheckout();
  const dataLayer = useDataLayer();

  const { services, totalExpensesFees } = useOrderDescription(order);

  const handleLockedBasket = ({ items }) => {
    dataLayer.setQueryData("currentBasketId", null);
    const id = uuidv4();
    saveBasket(
      {
        id,
        items,
      },
      {
        onSuccess: () => {
          dataLayer.setQueryData("currentBasketId", id);
        },
      }
    );
  };

  const handleRemoveSubscription = () => {
    const nonSubscriptionItems = currentBasket.items.filter(
      (basketItem) => basketItem.itemType !== "product"
    );
    const items: IUnsavedBasket["items"] = [
      ...nonSubscriptionItems.map(({ itemId, itemType, quantity }) => ({
        itemId,
        itemType,
        quantity,
      })),
    ];
    saveBasket(
      {
        id: currentBasket.id,
        items,
      },
      {
        onSuccess: () => {
          <Navigate {...rootRoute} />;
        },
        onError: (error: IErrorResponse) => {
          if (error.response?.status === 423) {
            handleLockedBasket({ items: nonSubscriptionItems });
          }
        },
      }
    );
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  let serviceOfferItemComponent = null;
  let serviceFeesComponent = null;

  if (services.length > 0) {
    const {
      rateCurrency,
      rateCents,
      billingCount,
      billingUnit,
      serviceProviderId,
    } = services[0]?.item;

    const { fees } = services[0];
    const { expenses } = services[0];

    serviceOfferItemComponent = (
      <ServiceOfferItem
        preview={preview}
        priceCents={rateCents}
        currency={rateCurrency}
        billingCount={billingCount}
        billingUnit={billingUnit}
        expenses={expenses}
        serviceProviderId={serviceProviderId}
      />
    );

    serviceFeesComponent = (
      <ServiceFees
        preview={preview}
        itemFeesCents={fees?.[0]?.amountCents}
        serviceFeeRate={fees?.[0]?.details?.percentage}
        billingUnit={billingUnit}
        billingCount={billingCount}
        currency={fees?.[0]?.amountCurrency}
        totalExpensesFees={totalExpensesFees}
      />
    );
  }

  return (
    <Card
      background={colorScheme === "sand" ? "sand.100" : "transparent"}
      shadow="none"
      padding={0}
      mb="5"
      {...props}
    >
      <List>
        {serviceOfferItemComponent}
        {serviceFeesComponent}
        <AdvancePaymentItem order={order} />
        <Credits order={order} />
        <SubscriptionItem
          order={order}
          handleRemoveSubscription={handleRemoveSubscription}
        />
        <Total order={order} colorScheme={colorScheme} preview={preview} />
      </List>
      <Savings order={order} />
    </Card>
  );
};
